/**
 * Adventure-level settings modal. Hosts everything that isn't
 * per-scene — start scene, tier table, item catalogue, share
 * config, export — by wrapping the existing `AdventureEditor`
 * in a dialog shell.
 *
 * This replaces the old right-panel "Inspect" tab's
 * adventure-level view. Scene editing moved fully inline (the
 * floating card on the graph), so the right panel became
 * Play-only and the adventure-wide config needed a new home.
 * A modal (reachable from the toolbar ⋯ menu / ⌘K) makes that
 * config reachable from ANY view mode — previously it was only
 * available in graph mode's right panel.
 *
 * Selecting a scene from inside the modal (the "+ add scene"
 * button's follow-up) closes the modal so the inline editor on
 * the new node becomes the focus.
 */
import { useEffect } from 'react';
import type { AdventureJson } from 'console-adventure';
import { AdventureEditor } from './AdventureEditor';
import { PANEL, PANEL_BORDER, PHOSPHOR, AMBER, DIM, TEXT, VOID } from '../lib/theme';

interface Props {
	json: AdventureJson;
	maxScore: number;
	onJsonChange: (next: AdventureJson, opts?: { remount?: boolean }) => void;
	onSelectScene: (id: string | null) => void;
	onClose: () => void;
}

export function AdventureSettingsDialog({
	json,
	maxScore,
	onJsonChange,
	onSelectScene,
	onClose
}: Props) {
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				onClose();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onClose]);

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="settings-dialog-title"
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
			style={{
				position: 'fixed',
				inset: 0,
				background: `${VOID}cc`,
				zIndex: 100,
				display: 'flex',
				alignItems: 'flex-start',
				justifyContent: 'center',
				paddingTop: '8vh',
				fontFamily: 'ui-monospace, "JetBrains Mono", monospace'
			}}
		>
			<div
				style={{
					width: 'min(520px, 94vw)',
					maxHeight: '82vh',
					background: PANEL,
					border: `1px solid ${PHOSPHOR}`,
					borderRadius: 11,
					boxShadow: `0 0 0 1px ${PHOSPHOR}22, 0 22px 60px #000000cc`,
					color: TEXT,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden'
				}}
			>
				{/* Header */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '13px 18px',
						borderBottom: `1px solid ${PANEL_BORDER}`,
						background: VOID
					}}
				>
					<span
						id="settings-dialog-title"
						style={{
							color: PHOSPHOR,
							fontWeight: 700,
							fontSize: 12,
							letterSpacing: '0.1em'
						}}
					>
						~/adventure · settings
					</span>
					<button
						onClick={onClose}
						title="Close (Esc)"
						style={{
							background: 'transparent',
							border: `1px solid ${PANEL_BORDER}`,
							borderRadius: 4,
							color: DIM,
							width: 22,
							height: 22,
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontFamily: 'inherit',
							fontSize: 12,
							cursor: 'pointer',
							lineHeight: 1
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.color = AMBER;
							e.currentTarget.style.borderColor = AMBER;
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.color = DIM;
							e.currentTarget.style.borderColor = PANEL_BORDER;
						}}
					>
						×
					</button>
				</div>

				{/* Body — the existing AdventureEditor, scrollable. */}
				<div style={{ flex: 1, overflow: 'auto', padding: '16px 18px' }}>
					<AdventureEditor
						json={json}
						maxScore={maxScore}
						onJsonChange={onJsonChange}
						onSelectScene={(id) => {
							// Adding / picking a scene from here closes the
							// modal so the inline editor on the graph takes
							// over for scene-level editing.
							onSelectScene(id);
							onClose();
						}}
					/>
				</div>
			</div>
		</div>
	);
}
